import type {Vec2, Vec2Simple} from "./VectorStructs.js";

export function GetPolygonBounds(points: Vec2Simple[]) {
	let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
	points.forEach(([x, y])=>{
		if (x < minX) minX = x;
		if (y < minY) minY = y;
		if (x > maxX) maxX = x;
		if (y > maxY) maxY = y;
	});
	return {minX, minY, maxX, maxY};
}

export function IsPointInPolygon(x: number, y: number, polygon: Vec2Simple[]) {
	// Standard ray-casting algorithm for point-in-polygon test
	let inside = false;
	for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
		const xi = polygon[i][0], yi = polygon[i][1];
		const xj = polygon[j][0], yj = polygon[j][1];

		const intersect = ((yi > y) !== (yj > y))
			&& (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
		if (intersect) inside = !inside;
	}

	return inside;
}

/** Remove collinear points. */
export function OptimizePolygon(polygon: Vec2Simple[]) {
	const optimized = [] as Vec2Simple[];

	for (let i = 0; i < polygon.length; i++) {
		const prev = i === 0 ? polygon[polygon.length - 1] : polygon[i - 1];
		const curr = polygon[i];
		const next = i === polygon.length - 1 ? polygon[0] : polygon[i + 1];

		// Check if points are collinear
		const dx1 = curr[0] - prev[0];
		const dy1 = curr[1] - prev[1];
		const dx2 = next[0] - curr[0];
		const dy2 = next[1] - curr[1];

		// Cross product near zero indicates collinearity
		const cross = dx1 * dy2 - dx2 * dy1;

		if (Math.abs(cross) > 0.001) {
			optimized.push(curr);
		}
	}

	return optimized;
}

// old version, that sometimes returned a tiny island that was just isolated, rather than the largest island
/*function TraceEdgesToPolygon(edges: Edge[], cellPositions: Vec2Simple[]) {
	if (edges.length === 0) return [];

	// Create a lookup from points to connected edges
	const pointToEdges = new Map<string, Edge[]>();

	for (const edge of edges) {
		const startKey = `${edge.start.x},${edge.start.y}`;
		const endKey = `${edge.end.x},${edge.end.y}`;

		if (!pointToEdges.has(startKey)) pointToEdges.set(startKey, []);
		if (!pointToEdges.has(endKey)) pointToEdges.set(endKey, []);

		pointToEdges.get(startKey)!.push(edge);
		pointToEdges.get(endKey)!.push(edge);
	}

	// Start with any edge
	const startEdge = edges[0];
	let currentPoint = startEdge.end;
	let currentKey = `${currentPoint.x},${currentPoint.y}`;
	let polygon = [startEdge.start, currentPoint];
	let usedEdges = new Set([0]);

	// Trace the path
	while (true) {
		// Find connected edges to the current point
		const connectedEdges = pointToEdges.get(currentKey)!;

		// Find unused edge
		let nextEdge: Edge | null = null;
		let edgeIndex = -1;

		for (let i = 0; i < connectedEdges.length; i++) {
			const edge = connectedEdges[i];
			const idx = edges.indexOf(edge);
			if (!usedEdges.has(idx)) {
				nextEdge = edge;
				edgeIndex = idx;
				break;
			}
		}

		// If no more edges, we're done
		if (!nextEdge) break;

		// Mark edge as used
		usedEdges.add(edgeIndex);

		// Find the other endpoint
		let nextPoint;
		if (nextEdge.start.x === currentPoint.x && nextEdge.start.y === currentPoint.y) {
			nextPoint = nextEdge.end;
		} else {
			nextPoint = nextEdge.start;
		}

		// Add point to polygon and continue
		polygon.push(nextPoint);
		currentPoint = nextPoint;
		currentKey = `${currentPoint.x},${currentPoint.y}`;

		// If we've reached the starting point, we're done
		if (polygon.length > 2 &&
			currentPoint.x === polygon[0].x &&
			currentPoint.y === polygon[0].y) {
			// Remove duplicate point
			polygon.pop();
			break;
		}
	}

	if (cellPositions.length > 100 && polygon.length < 10) debugger;

	return polygon;
}*/

export type Edge = {start: Vec2Simple, end: Vec2Simple};
export function TraceEdgesToPolygon(edges: Edge[]) {
	if (edges.length === 0) return [];

	// Create a lookup from points to connected edges
	const pointToEdges = new Map<string, Edge[]>();

	for (const edge of edges) {
		const startKey = `${edge.start[0]},${edge.start[1]}`;
		const endKey = `${edge.end[0]},${edge.end[1]}`;

		if (!pointToEdges.has(startKey)) pointToEdges.set(startKey, []);
		if (!pointToEdges.has(endKey)) pointToEdges.set(endKey, []);

		pointToEdges.get(startKey)!.push(edge);
		pointToEdges.get(endKey)!.push(edge);
	}

	// Find all separate edge islands by finding connected components
	const edgeIslands: Edge[][] = [];
	const visitedEdges = new Set<number>();

	for (let i = 0; i < edges.length; i++) {
		if (visitedEdges.has(i)) continue;

		// Start a new island
		const island: Edge[] = [];
		const queue: number[] = [i];

		while (queue.length > 0) {
			const edgeIdx = queue.shift()!;

			if (visitedEdges.has(edgeIdx)) continue;
			visitedEdges.add(edgeIdx);

			const edge = edges[edgeIdx];
			island.push(edge);

			// Find all connected edges through shared vertices
			const startKey = `${edge.start[0]},${edge.start[1]}`;
			const endKey = `${edge.end[0]},${edge.end[1]}`;

			// Process all edges connected to the start point
			for (const connectedEdge of pointToEdges.get(startKey)!) {
				const connIdx = edges.indexOf(connectedEdge);
				if (!visitedEdges.has(connIdx)) {
					queue.push(connIdx);
				}
			}

			// Process all edges connected to the end point
			for (const connectedEdge of pointToEdges.get(endKey)!) {
				const connIdx = edges.indexOf(connectedEdge);
				if (!visitedEdges.has(connIdx)) {
					queue.push(connIdx);
				}
			}
		}

		edgeIslands.push(island);
	}

	// For each island, trace its polygon
	const polygons: Vec2Simple[][] = [];

	for (const currentIsland_edges of edgeIslands) {
		// Skip tiny islands that are likely outliers
		if (currentIsland_edges.length < 4) continue;

		// Start with the first edge of this island
		const startEdge = currentIsland_edges[0];
		let currentPoint = startEdge.end;
		let currentKey = `${currentPoint[0]},${currentPoint[1]}`;
		const polygon = [startEdge.start, currentPoint];
		const usedEdges = new Set([currentIsland_edges.indexOf(startEdge)]);

		// Trace the path
		while (true) {
			// Find connected edges to the current point
			const connectedEdges = pointToEdges.get(currentKey)!.filter(e=>currentIsland_edges.includes(e));

			// Find unused edge
			let nextEdge: Edge | null = null;
			let edgeIndex = -1;

			for (let i = 0; i < connectedEdges.length; i++) {
				const edge = connectedEdges[i];
				const idx = currentIsland_edges.indexOf(edge);
				if (!usedEdges.has(idx)) {
					nextEdge = edge;
					edgeIndex = idx;
					break;
				}
			}

			// If no more edges, we're done with this loop
			if (!nextEdge) break;

			// Mark edge as used
			usedEdges.add(edgeIndex);

			// Find the other endpoint
			let nextPoint;
			if (nextEdge.start[0] === currentPoint[0] && nextEdge.start[1] === currentPoint[1]) {
				nextPoint = nextEdge.end;
			} else {
				nextPoint = nextEdge.start;
			}

			// Add point to polygon and continue
			polygon.push(nextPoint);
			currentPoint = nextPoint;
			currentKey = `${currentPoint[0]},${currentPoint[1]}`;

			// If we've reached the starting point, we're done
			if (polygon.length > 2 &&
				currentPoint[0] === polygon[0][0] &&
				currentPoint[1] === polygon[0][1]) {
				// Remove duplicate point
				polygon.pop();
				break;
			}
		}

		// Add this island's polygon to our collection
		polygons.push(polygon);

		// if not all edges in this edge-island were used, this must be a "kitty corner" situation (where two polygons were detected as one edge-island, due to sharing a corner -- despite being separate polygons)
		// resolve this "on the fly" by splitting the edge-island into two (the existing one, with its unused edges removed; and a new one, added to end of edge-islands list [to be processed soon], containing the unused edges)
		if (usedEdges.size < currentIsland_edges.length) {
			//console.warn(`Not all edges used in island: ${usedEdges.size}/${islandEdges.length}`);
			const currentIsland_edges_used = currentIsland_edges.filter((_, idx)=>usedEdges.has(idx));
			const currentIsland_edges_unused = currentIsland_edges.filter((_, idx)=>!usedEdges.has(idx));
			// "fix" existing island (removing edges that were not actually part of the polygon, ie. wrongly added to the island)
			currentIsland_edges.length = 0;
			currentIsland_edges.push(...currentIsland_edges_used);
			// add new island (containing the unused edges)
			edgeIslands.push(currentIsland_edges_unused);
		}
	}
	//console.log(`Edge islands found(${edgeIslands.length}):`, edgeIslands, "@edges:", edges);

	// If we have multiple polygons, select the largest one
	if (polygons.length > 0) {
		const polygonAreas = polygons.ToMap(a=>a, a=>CalculatePolygonArea(a));
		//console.log(`Polygons found(${polygons.length}):`, polygons, "@areas:", polygons.map(a=>polygonAreas.get(a)));
		return polygons.OrderByDescending(a=>polygonAreas.get(a)!)[0]; // Return the largest polygon
	}

	// Fallback: return an empty array if no valid polygons
	return [];
}

// Helper function to calculate polygon area using the Shoelace formula
export function CalculatePolygonArea(polygon: Vec2Simple[]): number {
	if (polygon.length < 3) return 0;

	let total = 0;
	for (let i = 0, l = polygon.length; i < l; i++) {
		const addX = polygon[i][0];
		const addY = polygon[i === polygon.length - 1 ? 0 : i + 1][1];
		const subX = polygon[i === polygon.length - 1 ? 0 : i + 1][0];
		const subY = polygon[i][1];

		total += (addX * addY * 0.5);
		total -= (subX * subY * 0.5);
	}

	return Math.abs(total);
}